import React, { useState } from 'react';
import { agentService } from '../API/agentService';
import './ProjectCreator.css';

const ProjectCreator = ({ projects, onProjectCreated, onProjectUpdated }) => {
  const [activeView, setActiveView] = useState('list'); // 'list' | 'create' | 'edit'
  const [editingProject, setEditingProject] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    velocityTarget: 5,
    filePatterns: [],
    phases: []
  });
  const [currentPhase, setCurrentPhase] = useState({
    name: '',
    tasks: []
  });
  const [currentTask, setCurrentTask] = useState('');
  const [currentFilePattern, setCurrentFilePattern] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      velocityTarget: 5,
      filePatterns: [],
      phases: []
    });
    setCurrentPhase({ name: '', tasks: [] });
    setCurrentTask('');
    setCurrentFilePattern('');
    setErrors({});
    setEditingProject(null);
  };

  const handleCreateNew = () => {
    resetForm();
    setActiveView('create');
  };

  const handleEditProject = (project) => {
    setFormData({
      name: project.name,
      description: project.description,
      velocityTarget: project.velocityTarget || 5,
      filePatterns: project.filePatterns || [],
      phases: project.phases || []
    });
    setEditingProject(project);
    setActiveView('edit');
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const addFilePattern = () => {
    if (currentFilePattern.trim()) {
      setFormData(prev => ({
        ...prev,
        filePatterns: [...prev.filePatterns, currentFilePattern.trim()]
      }));
      setCurrentFilePattern('');
    }
  };

  const removeFilePattern = (index) => {
    setFormData(prev => ({
      ...prev,
      filePatterns: prev.filePatterns.filter((_, i) => i !== index)
    }));
  };

  const addTaskToCurrentPhase = () => {
    if (currentTask.trim()) {
      setCurrentPhase(prev => ({
        ...prev,
        tasks: [...prev.tasks, currentTask.trim()]
      }));
      setCurrentTask('');
    }
  };

  const removeTaskFromCurrentPhase = (index) => {
    setCurrentPhase(prev => ({
      ...prev,
      tasks: prev.tasks.filter((_, i) => i !== index)
    }));
  };

  const addPhase = () => {
    if (currentPhase.name.trim() && currentPhase.tasks.length > 0) {
      setFormData(prev => ({
        ...prev,
        phases: [...prev.phases, { ...currentPhase }]
      }));
      setCurrentPhase({ name: '', tasks: [] });
    }
  };

  const removePhase = (index) => {
    setFormData(prev => ({
      ...prev,
      phases: prev.phases.filter((_, i) => i !== index)
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Project name is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Project description is required';
    }

    if (formData.velocityTarget < 1 || formData.velocityTarget > 20) {
      newErrors.velocityTarget = 'Velocity target must be between 1 and 20';
    }

    if (formData.phases.length === 0) {
      newErrors.phases = 'At least one phase is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      if (editingProject) {
        // Update existing project
        const result = await agentService.updateProject(editingProject.id, formData);
        if (result.success) {
          onProjectUpdated();
          setActiveView('list');
          resetForm();
        } else {
          setErrors({ submit: result.error });
        }
      } else {
        // Create new project
        const result = await agentService.createProject(formData);
        if (result.success) {
          onProjectCreated();
          setActiveView('list');
          resetForm();
        } else {
          setErrors({ submit: result.error });
        }
      }
    } catch (error) {
      setErrors({ submit: error.message });
    } finally {
      setLoading(false);
    }
  };

  const renderProjectList = () => (
    <div className="project-creator-list">
      <div className="list-header">
        <h3>Manage Projects</h3>
        <button className="btn btn-primary" onClick={handleCreateNew}>
          ➕ Create New Project
        </button>
      </div>

      <div className="projects-grid">
        {projects.map(project => (
          <div key={project.id} className="project-card">
            <div className="project-card-header">
              <h4>{project.name}</h4>
              <span className={`status-badge ${project.status}`}>
                {project.status}
              </span>
            </div>

            <p className="project-card-description">
              {project.description}
            </p>

            <div className="project-card-stats">
              <div className="stat">
                <span className="stat-label">Progress</span>
                <span className="stat-value">{project.completionPercentage}%</span>
              </div>
              <div className="stat">
                <span className="stat-label">Phase</span>
                <span className="stat-value">{project.currentPhase || 'Planning'}</span>
              </div>
              <div className="stat">
                <span className="stat-label">Velocity</span>
                <span className="stat-value">{project.velocityTarget} pts/day</span>
              </div>
            </div>

            <div className="project-card-actions">
              <button
                className="btn btn-secondary"
                onClick={() => handleEditProject(project)}
              >
                ✏️ Edit
              </button>
            </div>
          </div>
        ))}

        {projects.length === 0 && (
          <div className="empty-state">
            <h4>No projects yet</h4>
            <p>Create your first College Journey project to get started with agent coordination.</p>
            <button className="btn btn-primary" onClick={handleCreateNew}>
              Create First Project
            </button>
          </div>
        )}
      </div>
    </div>
  );

  const renderProjectForm = () => (
    <div className="project-creator-form">
      <div className="form-header">
        <h3>{editingProject ? 'Edit Project' : 'Create New Project'}</h3>
        <button
          className="btn btn-secondary"
          onClick={() => setActiveView('list')}
        >
          ← Back to List
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Basic Information */}
        <div className="form-section">
          <h4>Basic Information</h4>

          <div className="form-group">
            <label>Project Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="e.g., Smart Scheduling, Essay Development"
              className={errors.name ? 'error' : ''}
            />
            {errors.name && <span className="error-text">{errors.name}</span>}
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Brief description of what this project encompasses"
              rows={3}
              className={errors.description ? 'error' : ''}
            />
            {errors.description && <span className="error-text">{errors.description}</span>}
          </div>

          <div className="form-group">
            <label>Velocity Target (points per day)</label>
            <input
              type="number"
              min="1"
              max="20"
              value={formData.velocityTarget}
              onChange={(e) => handleInputChange('velocityTarget', parseInt(e.target.value))}
              className={errors.velocityTarget ? 'error' : ''}
            />
            {errors.velocityTarget && <span className="error-text">{errors.velocityTarget}</span>}
          </div>
        </div>

        {/* File Patterns */}
        <div className="form-section">
          <h4>File Patterns</h4>
          <p className="section-description">
            Specify which files belong to this project for agent monitoring
          </p>

          <div className="form-group">
            <div className="add-item-row">
              <input
                type="text"
                value={currentFilePattern}
                onChange={(e) => setCurrentFilePattern(e.target.value)}
                placeholder="e.g., src/components/College/Timeline/**"
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFilePattern())}
              />
              <button type="button" onClick={addFilePattern} className="btn btn-secondary">
                Add
              </button>
            </div>

            <div className="items-list">
              {formData.filePatterns.map((pattern, index) => (
                <div key={index} className="item-tag">
                  <span>{pattern}</span>
                  <button
                    type="button"
                    onClick={() => removeFilePattern(index)}
                    className="remove-btn"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Phases */}
        <div className="form-section">
          <h4>Project Phases</h4>
          <p className="section-description">
            Define the phases and tasks for this project
          </p>

          {/* Current Phase Being Created */}
          <div className="phase-creator">
            <div className="form-group">
              <label>Phase Name</label>
              <input
                type="text"
                value={currentPhase.name}
                onChange={(e) => setCurrentPhase(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Foundation, Development, Testing"
              />
            </div>

            <div className="form-group">
              <label>Tasks</label>
              <div className="add-item-row">
                <input
                  type="text"
                  value={currentTask}
                  onChange={(e) => setCurrentTask(e.target.value)}
                  placeholder="Add a task for this phase"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTaskToCurrentPhase())}
                />
                <button type="button" onClick={addTaskToCurrentPhase} className="btn btn-secondary">
                  Add Task
                </button>
              </div>

              <div className="items-list">
                {currentPhase.tasks.map((task, index) => (
                  <div key={index} className="item-tag">
                    <span>{task}</span>
                    <button
                      type="button"
                      onClick={() => removeTaskFromCurrentPhase(index)}
                      className="remove-btn"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <button
              type="button"
              onClick={addPhase}
              className="btn btn-primary"
              disabled={!currentPhase.name.trim() || currentPhase.tasks.length === 0}
            >
              Add Phase
            </button>
          </div>

          {/* Existing Phases */}
          <div className="phases-list">
            {formData.phases.map((phase, index) => (
              <div key={index} className="phase-item">
                <div className="phase-header">
                  <h5>{phase.name}</h5>
                  <button
                    type="button"
                    onClick={() => removePhase(index)}
                    className="btn btn-danger"
                  >
                    Remove
                  </button>
                </div>
                <div className="phase-tasks">
                  {phase.tasks.map((task, taskIndex) => (
                    <span key={taskIndex} className="task-tag">{task}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {errors.phases && <span className="error-text">{errors.phases}</span>}
        </div>

        {/* Submit */}
        <div className="form-actions">
          {errors.submit && (
            <div className="error-message">
              {errors.submit}
            </div>
          )}

          <div className="action-buttons">
            <button
              type="button"
              onClick={() => setActiveView('list')}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Saving...' : (editingProject ? 'Update Project' : 'Create Project')}
            </button>
          </div>
        </div>
      </form>
    </div>
  );

  return (
    <div className="project-creator">
      {activeView === 'list' ? renderProjectList() : renderProjectForm()}
    </div>
  );
};

export default ProjectCreator;